import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import gameService from '../../../utils/gameService';
import { generateGameUrl } from '../../../utils/urlUtils';

const HeroBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredGames, setFeaturedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load featured games from database
  useEffect(() => {
    const loadFeaturedGames = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all published games and filter for featured ones, or get trending games as fallback
        const result = await gameService.getPublishedGames();
        
        if (result.success && result.data) {
          // Filter for featured games first
          let featured = result.data.filter(game => game.featured === true);
          
          // If no featured games, use the most popular/trending games
          if (featured.length === 0) {
            featured = result.data
              .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
              .slice(0, 3);
          }
          
          // Transform games to match the expected format
          const transformedGames = featured.slice(0, 3).map(game => ({
            id: game.id,
            title: game.title,
            studio: game.gaming_studios?.name || 'Unknown Studio',
            description: game.description || 'No description available.',
            image: game.cover_image_url || '/assets/images/no_image.png',
            rating: game.average_rating || 0,
            genre: game.game_category_mappings?.[0]?.game_categories?.name || 'Game',
            playTime: game.estimated_play_time ? `${game.estimated_play_time}+ min` : 'Variable'
          }));
          
          setFeaturedGames(transformedGames);
        } else {
          setError(result.error || 'Failed to load featured games');
        }
      } catch (err) {
        console.error('Error loading featured games:', err);
        setError('Failed to load featured games');
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedGames();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredGames.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [featuredGames.length]);

  const currentGame = featuredGames[currentSlide];

  // Loading state
  if (loading) {
    return (
      <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Loading featured games...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-2">Failed to load featured games</p>
            <p className="text-slate-300 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // No games state
  if (featuredGames.length === 0) {
    return (
      <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Gamepad2" size={48} className="text-slate-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-2">No featured games available</p>
            <p className="text-slate-300 text-sm">Check back later for new games!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 to-slate-800">
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src={currentGame.image}
          alt={currentGame.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            {/* Game Info */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                  {currentGame.genre}
                </span>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-yellow-400 fill-current" />
                  <span className="text-sm text-slate-300">{currentGame.rating}</span>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                {currentGame.title}
              </h1>
              <p className="text-slate-300 text-sm mb-1">by {currentGame.studio}</p>
              <p className="text-slate-400 text-sm">{currentGame.playTime}</p>
            </div>

            {/* Description */}
            <p className="text-slate-200 text-base md:text-lg mb-6 leading-relaxed">
              {currentGame.description}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={generateGameUrl(currentGame)} state={{ game: currentGame }}>
                <Button 
                  variant="default" 
                  size="lg" 
                  iconName="Play" 
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Play Now
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="lg" 
                iconName="Info" 
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                More Info
              </Button>
              <Button 
                variant="ghost" 
                size="lg" 
                iconName="Heart" 
                className="w-full sm:w-auto"
              >
                Add to Favorites
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {featuredGames.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => setCurrentSlide((prev) => (prev - 1 + featuredGames.length) % featuredGames.length)}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
      >
        <Icon name="ChevronLeft" size={20} color="white" />
      </button>
      <button
        onClick={() => setCurrentSlide((prev) => (prev + 1) % featuredGames.length)}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
      >
        <Icon name="ChevronRight" size={20} color="white" />
      </button>
    </div>
  );
};

export default HeroBanner;
